/**
 * Keeps BundleUp-added gift/add-on lines in sync with their trigger product.
 *
 * BundleUp tags every line it touches with a hidden `_BU_main` property:
 * - On the trigger ("main") line, `_BU_main` equals that line's own product_id.
 * - On every gift/add-on line it spawns, `_BU_main` equals the trigger's product_id
 *   (which differs from the gift's own product_id).
 * `_BU_rule` is self-referential (equals the gift's own line id) and carries no
 * linking information, so it is intentionally ignored.
 *
 * Rules enforced here:
 * 1. A gift line is removed once its trigger product is no longer in the cart.
 * 2. Of the two mutually-exclusive shipping gifts (home delivery vs. pickup point
 *    delivery), home delivery always wins - pickup point delivery is removed
 *    whenever a (non-orphaned) home delivery line is present.
 * 3. Shipping gift lines are capped at quantity 1.
 * All resulting removals/clamps are sent as a single batched /cart/update.js call.
 *
 * DOM sync deliberately avoids Dawn's full cart-drawer/cart-items section
 * refetch-and-replace: BundleUp renders its own widgets into that same DOM
 * region, and a broad outerHTML swap can race with BundleUp's own re-render
 * and leave the drawer showing the wrong content. Instead, rows are targeted
 * directly via `data-cart-key` (added to the cart-item <tr> in
 * snippets/cart-drawer.liquid and sections/main-cart-items.liquid) and only
 * those specific rows are touched. The `.totals`/`.discounts` blocks in each
 * footer are patched from server-rendered section HTML (requested in the same
 * batched call) so the subtotal stays correct without reimplementing money
 * formatting in JS.
 */
(function () {
  var HOME_SHIPPING_HANDLE = 'tyvexo-home-shipping';
  var PICKUP_SHIPPING_HANDLE = 'tyvexo-pickup-shipping';

  var isCleaning = false;
  var pendingRerun = false;

  function isBundleUpGift(item) {
    var mainId = item.properties && item.properties._BU_main;
    if (!mainId) return false;
    return Number(mainId) !== item.product_id;
  }

  function isShippingGift(item) {
    return item.handle === HOME_SHIPPING_HANDLE || item.handle === PICKUP_SHIPPING_HANDLE;
  }

  // Returns { updates, removedKeys, clampedKeys } - updates is the payload
  // for /cart/update.js, the key sets drive the surgical DOM sync afterward.
  function computeUpdates(cart) {
    var productIdsInCart = new Set(cart.items.map(function (item) { return item.product_id; }));
    var updates = {};
    var orphanKeys = new Set();
    var clampedKeys = new Set();

    // Rule 1: remove gifts whose trigger product is no longer in the cart.
    cart.items.forEach(function (item) {
      if (!isBundleUpGift(item)) return;
      var mainId = Number(item.properties._BU_main);
      if (!productIdsInCart.has(mainId)) {
        orphanKeys.add(item.key);
        updates[item.key] = 0;
      }
    });

    // Rule 2: home delivery beats pickup point delivery - they can't coexist.
    var homeSurvives = cart.items.some(function (item) {
      return item.handle === HOME_SHIPPING_HANDLE && !orphanKeys.has(item.key);
    });
    if (homeSurvives) {
      cart.items.forEach(function (item) {
        if (item.handle === PICKUP_SHIPPING_HANDLE) {
          orphanKeys.add(item.key);
          updates[item.key] = 0;
        }
      });
    }

    // Rule 3: shipping gift lines never exceed quantity 1 (skip lines already
    // slated for removal above).
    cart.items.forEach(function (item) {
      if (isShippingGift(item) && item.quantity > 1 && updates[item.key] === undefined) {
        updates[item.key] = 1;
        clampedKeys.add(item.key);
      }
    });

    return { updates: updates, removedKeys: orphanKeys, clampedKeys: clampedKeys };
  }

  // Sections whose totals/discounts blocks need to stay accurate. 'cart-drawer'
  // is a fixed, single-instance section; main-cart-items/main-cart-footer are
  // templated, so their real section ids have to be read off the live DOM
  // (Dawn's own convention). main-cart-items is only used for the empty-cart
  // case (see replaceWithEmptyState) but is requested unconditionally since
  // the outcome isn't known in advance.
  function getSectionsToRequest() {
    var sections = [];
    if (document.querySelector('cart-drawer-items')) {
      sections.push('cart-drawer');
    }
    var mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push(mainCartItems.dataset.id);
    }
    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push(mainCartFooter.dataset.id);
    }
    return sections;
  }

  // Patches only the totals/discounts blocks inside one footer scope with the
  // freshly server-rendered equivalent (keeps correct money formatting without
  // touching the cart-item rows or any BundleUp-owned markup nearby).
  function patchTotals(scopeSelector, html) {
    var liveScope = document.querySelector(scopeSelector);
    if (!liveScope || !html) return;
    var freshDoc = new DOMParser().parseFromString(html, 'text/html');

    ['.totals', '.discounts'].forEach(function (selector) {
      var liveNode = liveScope.querySelector(selector);
      var freshNode = freshDoc.querySelector(selector);
      if (liveNode && freshNode) {
        liveNode.replaceWith(freshNode);
      } else if (liveNode && !freshNode) {
        liveNode.remove();
      }
    });
  }

  // Mirrors Dawn's own empty-cart handling in CartItems.updateQuantity()
  // (assets/cart.js): only classList.toggle('is-empty', ...) plus an
  // innerHTML replace scoped to .drawer__inner / .js-contents - never the
  // whole #CartDrawer and never the #CartDrawer-Overlay node, and never a
  // wholesale className overwrite. Replacing the whole #CartDrawer destroys
  // the overlay's close-on-click listener (leaving the drawer stuck), and
  // overwriting className wipes out the active/animate classes Dawn's own
  // open() method adds (silently closing the drawer) - both avoided here by
  // touching only the same inner scopes Dawn's own code touches.
  function replaceWithEmptyState(sectionsHtml) {
    if (sectionsHtml['cart-drawer']) {
      var freshDrawerDoc = new DOMParser().parseFromString(sectionsHtml['cart-drawer'], 'text/html');
      var freshInner = freshDrawerDoc.querySelector('#CartDrawer .drawer__inner');
      var liveInner = document.querySelector('#CartDrawer .drawer__inner');
      if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;

      var liveDrawer = document.querySelector('cart-drawer');
      if (liveDrawer) liveDrawer.classList.toggle('is-empty', true);
    }

    var mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id && sectionsHtml[mainCartItems.dataset.id]) {
      var freshItemsDoc = new DOMParser().parseFromString(sectionsHtml[mainCartItems.dataset.id], 'text/html');
      var freshJsContents = freshItemsDoc.querySelector('#main-cart-items .js-contents');
      var liveJsContents = mainCartItems.querySelector('.js-contents');
      if (freshJsContents && liveJsContents) liveJsContents.innerHTML = freshJsContents.innerHTML;

      var liveCartItems = document.querySelector('cart-items');
      if (liveCartItems) liveCartItems.classList.toggle('is-empty', true);
    }

    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id && sectionsHtml[mainCartFooter.dataset.id]) {
      var freshFooterDoc = new DOMParser().parseFromString(sectionsHtml[mainCartFooter.dataset.id], 'text/html');
      var freshFooter = freshFooterDoc.getElementById('main-cart-footer');
      if (freshFooter) mainCartFooter.innerHTML = freshFooter.innerHTML;
      mainCartFooter.classList.toggle('is-empty', true);
    }
  }

  function applyDomUpdates(removedKeys, clampedKeys, cartData) {
    document.querySelectorAll('[data-cart-key]').forEach(function (row) {
      var key = row.getAttribute('data-cart-key');
      if (removedKeys.has(key)) {
        row.remove();
        return;
      }
      if (clampedKeys.has(key)) {
        var input = row.querySelector('input[type="number"]');
        if (input) {
          input.value = 1;
          input.setAttribute('value', 1);
        }
      }
    });

    if (typeof cartData.item_count === 'number') {
      // .cart-count-bubble:empty is hidden via CSS (assets/base.css), so at
      // 0 the bubble must be emptied, not just have its number zeroed -
      // otherwise the badge keeps showing a stale count.
      document.querySelectorAll('.cart-count-bubble').forEach(function (bubble) {
        if (cartData.item_count === 0) {
          bubble.innerHTML = '';
          return;
        }
        var span = bubble.querySelector('span[aria-hidden="true"]');
        if (span) span.textContent = cartData.item_count;
      });
    }

    if (cartData.item_count === 0) {
      replaceWithEmptyState(cartData.sections || {});
      return;
    }

    var sectionsHtml = cartData.sections || {};
    if (sectionsHtml['cart-drawer']) {
      patchTotals('.cart-drawer__footer', sectionsHtml['cart-drawer']);
    }
    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id && sectionsHtml[mainCartFooter.dataset.id]) {
      patchTotals('#main-cart-footer', sectionsHtml[mainCartFooter.dataset.id]);
    }
  }

  function runCleanup() {
    if (isCleaning) {
      pendingRerun = true;
      return;
    }
    isCleaning = true;

    fetch(window.routes.cart_url + '.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var result = computeUpdates(cart);
        if (Object.keys(result.updates).length === 0) return;

        return fetch(window.routes.cart_update_url, Object.assign({}, fetchConfig(), {
          body: JSON.stringify({
            updates: result.updates,
            sections: getSectionsToRequest(),
            sections_url: window.location.pathname,
          }),
        }))
          .then(function (response) { return response.json(); })
          .then(function (cartData) {
            applyDomUpdates(result.removedKeys, result.clampedKeys, cartData);
          });
      })
      .catch(function (error) {
        console.error('tyvexo-bundleup-gift-sync: cleanup failed', error);
      })
      .finally(function () {
        isCleaning = false;
        if (pendingRerun) {
          pendingRerun = false;
          runCleanup();
        }
      });
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
    runCleanup();
  });

  document.addEventListener('DOMContentLoaded', runCleanup);
})();
